import { optionService } from "../../option";
import {
    RegionElement,
    StepService,
    CommandWithFollowup,
    FrameData,
    ColorOption,
    GlobalPropertyStateFileUploadStorage,
    FrameStepData,
    Step,
    LayoutsState,
    VariantResource,
    Asset,
    Region,
    ILayout,
    FrameElement,
    LayoutElementType,
    ColorDefinition,
} from "../../../types";
import { Poller } from "../../../util/Poller";
import { WorkflowManager } from "../../../WorkflowManager";
import { assetService } from "../../asset";
import { FrameService } from "./Service";
import { LayoutNotFoundError, ResourceGenerationError, ResourceNotFoundError } from "../../../util/exception";
import { AssetNotFoundError } from "../../../util/exception";
import { browserColorToHex } from "../../../util/color";
import { fetch, fetchAsArrayBuffer, fetchAsString } from "../../../util/crossplatform";
import {
    calculateOffsets,
    generateDefaultRectangleFrameSvg,
    getFrameData,
    GetSVGDimensions,
    patternImageDataCache,
} from "../../../util/frame";
import { CreateElementCommand, DeleteElementCommand, GroupCommand } from "../../../command";
import { generateSVGWithUnknownColors } from "../../../util/illustration";
import { getAttributesFromArrayBuffer } from "../../../util/image";
import { generate } from "../../../util/guid";
import { FrameStepHandle } from "../../../stepHandles/steps/frame";

export interface FrameCreateOpts {
    stepName?: string;
    frameData: FrameData;
    disablePlaceholder?: boolean;
    pattern?: any;
}

class FrameStepService implements StepService<FrameStepData> {
    async init(
        stepData: Step<FrameStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        // If existing state then use it.
        const reducerStep = reducerState?.serializableWorkflow.steps.find(
            (step) => step.stepName === stepData.stepName,
        );
        if (
            (reducerStep?.storage && Object.keys(reducerStep.storage).length !== 0) ||
            (reducerStep?.selectedVariants && reducerStep.selectedVariants.length !== 0)
        ) {
            await this.reload(stepData, workflowManager, reducerState!);
            return null;
        }

        if (stepData.mandatory) {
            workflowManager.setMandatoryFulfilled(stepData.stepName, false);
        }
        workflowManager.markStepsAsInitialised([stepData.stepName]);

        // When a bundle is present we load the frame aspect.
        let initialPatternOverride: string | undefined = undefined;
        const bundle = workflowManager.getWorkflowExperience().getBundle();
        if (bundle && stepData.globalPropertyAspectConfigurations) {
            const stateManager = bundle.getGlobalPropertyStateManager();
            // FIXME: This might not be robust enough. I think we support more than Upload on frames.
            const uploadAspect = stepData.globalPropertyAspectConfigurations[0];
            const key = stateManager.getAspectStorage<GlobalPropertyStateFileUploadStorage>(uploadAspect.aspectName!);
            if (key && key.originalAssetKey) {
                initialPatternOverride = (await assetService.getLocalOrFromServer(key.originalAssetKey)).fileLink;
            }
        }

        // If an option exists then determine if there's a default variant.
        if (stepData.option && stepData.option.variants && stepData.option.variants.length > 0) {
            const option = stepData.option;
            const defaultVariant = optionService.getDefaultVariant(option);
            if (defaultVariant) {
                return this.selectVariantCommand(
                    stepData,
                    defaultVariant,
                    [],
                    workflowManager,
                    undefined,
                    initialPatternOverride ? initialPatternOverride : stepData.data.placeholderImageUrl,
                );
            }
            return null;
        }

        // If no option then create a square box.
        return this.selectVariantCommand(
            stepData,
            undefined,
            [],
            workflowManager,
            undefined,
            initialPatternOverride ? initialPatternOverride : stepData.data.placeholderImageUrl,
        );
    }

    private async reload(stepData: Step<FrameStepData>, workflowManager: WorkflowManager, reducerState: LayoutsState) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const regionElements = elementsForStep.map((el) => ({ id: el.id, region: el.stepRegion }));
        if (!stepData.option || (stepData.option.variants || []).length === 0) {
            const frameSvgs = stepData.data.regions.map((region) => generateDefaultRectangleFrameSvg(region));
            workflowManager.updateStorage(stepData.stepName, {
                currentFrameSources: frameSvgs,
            });
        }
        if (serializedStep) {
            const finish = async () => {
                const storageColors = serializedStep.storage?.colors;
                const patternSrc = serializedStep.storage?.framePatternSrc;
                const offsets = serializedStep.storage?.frameOffsetsList;
                workflowManager.updateMetadata(stepData.stepName, {
                    frameOffsetsList: offsets,
                    framePatternSrc: patternSrc,
                });
                if (patternSrc) {
                    const frameService = (
                        workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as
                            | undefined
                            | FrameStepHandle
                    )?.frameService;
                    if (!frameService) throw new Error("Frame service unavailable, cannot load pattern!");
                    // Load the pattern and recalculate offsets. Deserialized offsets will potentially be applied after.
                    await this.loadPatternFromString(patternSrc, frameService, true, storageColors);
                    workflowManager.updateMetadata(stepData.stepName, { image: patternSrc });
                    workflowManager.updateStorage(stepData.stepName, {
                        framePatternSrc: patternSrc,
                    });
                    this.validateColorCount(stepData, workflowManager);
                }
                if (offsets?.some((offset) => offset.zoom)) {
                    const frameService = (
                        workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as
                            | undefined
                            | FrameStepHandle
                    )?.frameService;
                    if (!frameService) throw new Error("Frame service unavailable, cannot load pattern!");
                    frameService.updateOffsets(offsets);
                }
                workflowManager.setMandatoryFulfilled(stepData.stepName, true);
            };

            if (serializedStep.selectedVariants && serializedStep.selectedVariants.length > 0) {
                const variantId = serializedStep.selectedVariants[0].id;
                if (stepData.option && variantId) {
                    const variant = stepData.option.variants?.find((variant) => variant.id === variantId);
                    if (variant) {
                        await workflowManager.setSelectionsAndElements(
                            stepData.stepName,
                            [variant],
                            regionElements,
                            async () => {
                                const frameSvgs = await Promise.all(
                                    stepData.data.regions.map((region) => this.frameSourceSvg(variant, region)),
                                );
                                workflowManager.updateStorage(stepData.stepName, { currentFrameSources: frameSvgs });
                                await finish();
                            },
                        );
                    }
                }
            } else {
                await workflowManager.setSelectionsAndElements(stepData.stepName, [], regionElements, finish);
            }
        }
    }

    async availableColors(stepData: Step<FrameStepData>, _workflowManager: WorkflowManager): Promise<ColorOption[]> {
        const colorOption = stepData.data.colorOption;
        return (
            colorOption?.variants?.map((variant: VariantResource) => {
                return {
                    fill: variant.color,
                    stroke: variant.color,
                    variant,
                    pmsValue: variant.name,
                };
            }) ?? []
        );
    }

    selectImage(
        stepData: Step<FrameStepData>,
        asset: Asset,
        workflowManager: WorkflowManager,
        recalculateOffsets = true,
    ): Promise<void> {
        const assetSrc = FrameStepService.patternSource(asset);
        const existingSrc = workflowManager.getStepStorage(stepData.stepName)?.framePatternSrc;
        if (existingSrc && existingSrc === assetSrc) {
            return Promise.resolve();
        }
        workflowManager.setEditedStatus(stepData.stepName, true);
        const src = asset.fileLink || "";

        // TODO: Use mime type instead of extension
        if (src.endsWith(".pdf") || src.endsWith(".eps") || src.endsWith(".ai")) {
            // In the case of PDF we need to transcode first as
            // we don't have first class support for PDF files in frames.
            return new Promise<void>((resolve) => {
                workflowManager.addPoller(
                    new Poller(
                        async () => {
                            const assetResponse = await assetService.getFromServer(asset.key || "");
                            const svgVersion = assetResponse.versions?.find((v) => v.name === "svg");
                            if (!svgVersion) return false;
                            const fetchResult = await fetch(svgVersion.link);
                            return fetchResult.status === 200;
                        },
                        () => {
                            assetService.getLocalOrFromServer(asset.key || "").then((response) => {
                                this.loadPatternFromAsset(response, stepData, workflowManager, recalculateOffsets).then(
                                    resolve,
                                );
                            });
                        },
                        () => {
                            throw new ResourceGenerationError("Failed to resolve transcoded PDF");
                        },
                    ),
                );
            });
        } else {
            return this.loadPatternFromAsset(asset, stepData, workflowManager, recalculateOffsets);
        }
    }

    async selectVariant(
        stepData: Step<FrameStepData>,
        variant: VariantResource | undefined,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
        setFrameIsUpdating?: (status: boolean) => void,
    ) {
        const result = await this.selectVariantCommand(
            stepData,
            variant,
            elements,
            workflowManager,
            setFrameIsUpdating,
        );
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    getCreateElementCommand(id: string, region: Region, layout: ILayout, options: FrameCreateOpts) {
        return new CreateElementCommand<FrameElement>(
            {
                id,
                type: LayoutElementType.Frame,
                x: region.left,
                y: region.top,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                rotation: region.rotation,
                scaleX: region.width / options.frameData.width,
                scaleY: region.height / options.frameData.height,
                path: options.frameData.path,
                dataWidth: options.frameData.width,
                dataHeight: options.frameData.height,
                stepRegion: region,
                stepName: options.stepName,
                disablePlaceholder: options.disablePlaceholder,
                pattern: options.pattern,
                immutable: region.immutable,
            },
            layout,
        );
    }

    async loadPatternFromString(
        src: string,
        frameService: FrameService,
        recalculateOffsets = true,
        colors:
            | {
                  [key: string]: ColorDefinition;
              }
            | undefined = undefined,
        colorSafeSvg?: boolean,
    ) {
        if (src.endsWith("svg")) {
            const svgData = await GetSVGDimensions(src);
            const width = svgData.width;
            const height = svgData.height;
            const originalSvg = await fetchAsString(src);
            if (colorSafeSvg) {
                const matches = originalSvg.match(/<(image|linearGradient|radialGradient)(\s|>|\/>)/gim);
                if (matches && matches.length > 0) {
                    throw new Error(
                        "Vector files may not include images or gradients when a color limit is specified.",
                    );
                }
            }
            const svgAndColors = await generateSVGWithUnknownColors(originalSvg);
            const data = {
                src: src,
                width,
                height,
                aspect: width / height,
                svg: svgAndColors.svg,
                colors: colors ?? svgAndColors.colors,
            };
            patternImageDataCache.set(src, data);
            frameService.setPatternData(data, recalculateOffsets);
        } else {
            const arrayBuffer = await fetchAsArrayBuffer(src, true);
            const attributes = await getAttributesFromArrayBuffer(arrayBuffer);
            const data = {
                src: src,
                width: attributes.width,
                height: attributes.height,
                aspect: attributes.width / attributes.height,
                svg: undefined,
                colors: undefined,
            };
            patternImageDataCache.set(src, data);
            frameService.setPatternData(data, recalculateOffsets);
        }
    }

    changeColors(
        stepData: Step<FrameStepData>,
        workflowManager: WorkflowManager,
        newFills: Map<string, ColorDefinition>,
    ) {
        const frameService = (
            workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as undefined | FrameStepHandle
        )?.frameService;
        if (!frameService || !frameService.getImageData()?.svg) {
            console.warn("changeColors was called without an SVG being assigned to a frame step");
            return;
        }
        const colorData = this.calculateColorMetadata(frameService, stepData, newFills);
        workflowManager.updateMetadata(stepData.stepName, { colors: colorData.metadataColors });
        workflowManager.updateStorage(stepData.stepName, {
            colors: colorData.storageColors,
        });
        // Update image data without updating offsets
        frameService.setPatternData(
            {
                ...frameService.getImageData()!,
                colors: colorData.newColors,
            },
            false,
        );
        this.validateColorCount(stepData, workflowManager);
    }

    getUniqueColorCount(stepData: Step<FrameStepData>, workflowManager: WorkflowManager): number {
        const frameService = (
            workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as undefined | FrameStepHandle
        )?.frameService;
        if (!frameService) return 0;
        const colors = frameService.getImageData()?.colors ?? {};
        const unique: string[] = [];
        for (const key in colors) {
            const color = colors[key];
            if (color.pmsValue) {
                const pms = color.pmsValue.trim().toLocaleLowerCase();
                if (!unique.includes(pms)) {
                    unique.push(pms);
                }
            } else if (color.browserValue) {
                // browserColorToHex already sanitizes value
                const hex = browserColorToHex(color.browserValue);
                if (!unique.includes(hex)) {
                    unique.push(hex);
                }
            }
        }
        return unique.length;
    }

    private calculateColorMetadata(
        frameService: FrameService,
        stepData: Step<FrameStepData>,
        newFills: Map<string, ColorDefinition>,
    ) {
        if (!frameService.getImageData()?.svg) {
            return {
                newColors: undefined,
                metadataColors: undefined,
                storageColors: undefined,
            };
        }
        // Compute change in color and apply to metadata
        const newColors: { [key: string]: ColorDefinition } = { ...(frameService.getImageData()!.colors ?? {}) };
        const storageColors: any = {};
        Object.entries(newColors).forEach(([key, value]) => {
            const color: any = { browserValue: value.browserValue };
            storageColors[key] = color;
        });
        for (const [className, newFill] of newFills.entries()) {
            newColors[className] = { browserValue: newFill.browserValue, pmsValue: newFill.pmsValue };
            storageColors[className] = newFill;
        }

        let colors = Array.from(Object.values(newColors)).map((c) => c.browserValue);
        const colorOption = stepData.data.colorOption;
        if (colorOption) {
            colorOption.variants?.forEach((v: VariantResource) => {
                colors = colors.map((c) => (c.toLowerCase() === v.color?.toLowerCase() ? v.name : c));
            });
        }
        return {
            newColors,
            metadataColors: colors,
            storageColors,
        };
    }

    private validateColorCount(stepData: Step<FrameStepData>, workflowManager: WorkflowManager) {
        const maxColors = stepData.data.colorPickerEnabled ? stepData.data.maxColors ?? 0 : 0;
        workflowManager.setStepError(
            stepData.stepName,
            "colors",
            maxColors > 0 && this.getUniqueColorCount(stepData, workflowManager) > maxColors
                ? "workflow.steps.frame.tooManyColors"
                : undefined,
        );
    }

    private async selectVariantCommand(
        stepData: Step<FrameStepData>,
        variant: VariantResource | undefined,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
        setFrameIsUpdating?: (status: boolean) => void,
        loadedPattern?: string,
    ): Promise<CommandWithFollowup | null> {
        const frameService = (
            workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as undefined | FrameStepHandle
        )?.frameService;
        if (!frameService) throw new Error("Frame service unavailable, cannot load pattern!");
        const frameSvgs = await Promise.all(
            stepData.data.regions.map((region) => this.frameSourceSvg(variant, region)),
        );
        setFrameIsUpdating && setFrameIsUpdating(true);
        const initZoom = stepData.data.initialZoomLevel;
        const createdData = await Promise.all(
            stepData.data.regions.map(async (r, index) => {
                const newFrameData = await getFrameData(frameSvgs[index]);
                const existingImage = frameService.getImageData();
                const removeExistingCommands = elements.map((elem) => new DeleteElementCommand(elem.id));
                const newOffsets = existingImage
                    ? calculateOffsets(
                          existingImage,
                          newFrameData,
                          initZoom && !stepData.data.forceImageCover ? { scale: initZoom } : undefined,
                          stepData.data.forceImageCover,
                      )
                    : undefined;
                const pattern = existingImage
                    ? {
                          id: generate(),
                          src: existingImage.src,
                          x: newOffsets?.x || 0,
                          y: newOffsets?.y || 0,
                          width: existingImage.width,
                          height: existingImage.height,
                          scaleX: newOffsets?.zoom || 1,
                          scaleY: newOffsets?.zoom || 1,
                          rotation: 0,
                      }
                    : undefined;

                const newElementId = generate();
                const layouts = workflowManager.getLayouts();
                const layout = layouts.find((layout: ILayout) => layout.panelId === r.panelId);
                if (!layout) {
                    throw new LayoutNotFoundError(r);
                }
                return {
                    command: this.getCreateElementCommand(newElementId, r, layout, {
                        frameData: newFrameData,
                        pattern,
                        disablePlaceholder: stepData.data.disablePlaceholder,
                        stepName: stepData.stepName,
                    }),
                    regionEl: {
                        id: newElementId,
                        region: r,
                    },
                    removeExistingCommands,
                };
            }),
        );

        const createCommands = createdData.map((d) => d.command);
        const removeExistingCommands = createdData.map((d) => d.removeExistingCommands).flat();
        return {
            command: new GroupCommand([...createCommands, ...removeExistingCommands]),
            followup: async () => {
                setFrameIsUpdating && setFrameIsUpdating(false);
                await workflowManager.setSelectionsAndElements(
                    stepData.stepName,
                    variant ? [variant] : [],
                    [...createdData.map((d) => d.regionEl)],
                    async () => {
                        workflowManager.updateStorage(stepData.stepName, { currentFrameSources: frameSvgs });
                        if (loadedPattern) {
                            const frameService = (
                                workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as
                                    | undefined
                                    | FrameStepHandle
                            )?.frameService;
                            if (!frameService) throw new Error("Frame service unavailable, cannot load pattern!");
                            await this.loadPatternFromString(loadedPattern, frameService, true);
                        }
                    },
                );
            },
        };
    }

    private frameSourceSvg = async (variant: undefined | VariantResource, region: Region): Promise<string> => {
        if (!variant) {
            return generateDefaultRectangleFrameSvg(region);
        }
        const asset = variant.asset;
        if (!asset) {
            throw new AssetNotFoundError(variant);
        }
        const link = asset.fileLink;
        if (link) {
            return fetchAsString(link, true);
        }
        throw new ResourceNotFoundError(asset);
    };

    /**
     * Grab the source to be used for a frame from the variant or throw otherwise.
     */
    static patternSource(asset: Asset) {
        const svgVersion = asset.versions?.find((v) => v.name === "svg");
        if (svgVersion?.link) {
            return svgVersion.link;
        }
        const cdnLink = asset.fileLink;
        if (cdnLink) {
            return cdnLink;
        }
        throw new ResourceNotFoundError(asset);
    }

    private async loadPatternFromAsset(
        asset: Asset,
        stepData: Step<FrameStepData>,
        workflowManager: WorkflowManager,
        recalculateOffsets: boolean,
    ): Promise<void> {
        const src = FrameStepService.patternSource(asset);
        const updateId = workflowManager.markUpdatePending();
        const frameService = (
            workflowManager.getWorkflowExperience().getStepById(stepData.stepName) as undefined | FrameStepHandle
        )?.frameService;
        if (!frameService) {
            workflowManager.markUpdateCompleted(updateId);
            throw new Error("Frame service unavailable, cannot load pattern!");
        }
        try {
            await this.loadPatternFromString(
                src,
                frameService,
                recalculateOffsets,
                undefined,
                stepData.data.colorPickerEnabled && !!stepData.data.maxColors && stepData.data.maxColors > 0,
            );
        } catch (e: any) {
            workflowManager.markUpdateCompleted(updateId);
            throw e;
        }
        const colorData = this.calculateColorMetadata(
            frameService,
            stepData,
            new Map(Object.entries(frameService.getImageData()?.colors ?? {})),
        );
        workflowManager.updateMetadata(stepData.stepName, { image: src, colors: colorData.metadataColors });
        workflowManager.updateStorage(stepData.stepName, {
            framePatternSrc: src,
            colors: colorData.storageColors,
        });
        workflowManager.markUpdateCompleted(updateId);

        workflowManager.setMandatoryFulfilled(stepData.stepName, true);
        workflowManager.setInformationResults(
            workflowManager.getInformationResults().filter((it) => it.stepID === stepData.stepName),
        );
        this.validateColorCount(stepData, workflowManager);
    }
}

export const frameStepService = new FrameStepService();
